import type {DeletedDiff} from '@github-ui/code-view-types'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {getLineNumberWidth} from '@github-ui/diffs/diff-line-helpers'
import {DiffSquares, UnifiedDiffLines} from '@github-ui/diffs/DiffParts'
import type {SimpleDiffLine} from '@github-ui/diffs/types'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {IconButton, Link, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'

import styles from './DeleteViewEntry.module.css'

export function DiffEntry({diff, index}: {diff: DeletedDiff; index: number}) {
  const [collapsed, setCollapsed] = useState(false)
  const [diffLines, setDiffLines] = useState<SimpleDiffLine[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const squares: Array<'addition' | 'deletion' | 'neutral'> = []
  for (let i = 0; i < 5; i++) {
    if (diff.deletions > i) {
      squares.push('deletion')
    } else {
      squares.push('neutral')
    }
  }

  const loadDiff = useCallback(async () => {
    if (!diff.loadDiffPath) return
    setLoading(true)
    setError(null)
    try {
      const response = await verifiedFetchJSON(diff.loadDiffPath)
      if (!response.ok) {
        setError(`Failed to load diff: ${response.status}`)
        return
      }
      const data: {diffLines: SimpleDiffLine[]} = await response.json()
      setDiffLines(data.diffLines)
    } catch {
      setError('Failed to load diff')
    } finally {
      setLoading(false)
    }
  }, [diff.loadDiffPath])

  if (diff.diffHTML) {
    return (
      <div id="readme" className="readme prose-diff html-blob blob">
        <VerifiedHTMLDiv html={diff.diffHTML} className="markdown-body container-lg" />
      </div>
    )
  }

  return (
    <div className={styles.diffEntry} id={`diff-entry-${index}`}>
      <div className={styles.diffHeader}>
        <IconButton
          aria-label={collapsed ? 'Expand diff' : 'Collapse diff'}
          icon={collapsed ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          tooltipDirection="s"
          variant="invisible"
          onClick={() => setCollapsed(prev => !prev)}
        />
        <span className="color-fg-muted">{diff.deletions}</span>
        <DiffSquares squares={squares} />
        <Link href={`#diff-entry-${index}`} className="color-fg-default">
          {diff.path}
        </Link>
        <CopyToClipboardButton textToCopy={diff.path} ariaLabel={'Copy path to clipboard'} />
      </div>
      {collapsed ? null : (
        <div className={diffLines || loading ? styles.diffContentCollapsed : styles.diffContent} tabIndex={-1}>
          {diffLines ? (
            <UnifiedDiffLines lines={diffLines} lineWidth={getLineNumberWidth(diffLines)} tabSize={4} />
          ) : loading ? (
            <div className={styles.loadingSpinner}>
              <Spinner />
            </div>
          ) : error ? (
            <div className={styles.diffOverlay} role="alert">
              {error}
            </div>
          ) : (
            <>
              <DiffPlaceholderSvg />
              <div className={styles.diffOverlay}>
                <Link as="button" onClick={loadDiff} inline>
                  Load diff
                </Link>
                This file was deleted.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function DiffPlaceholderSvg() {
  return (
    <svg
      aria-hidden="true"
      className={clsx('width-full', styles.placeholderSvg)}
      viewBox="0 0 340 84"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className={clsx('js-diff-placeholder', styles.placeholderPath)}
        clipPath="url(#diff-placeholder)"
        d="M0 0h340v84H0z"
        fillRule="evenodd"
      />
    </svg>
  )
}
